import React from 'react';
import { Calendar, Sparkles } from 'lucide-react';
import { parseISO } from 'date-fns';
import { BeltRankValue, PractitionerProfile } from 'app/javascript/types';
import BeltImage from 'app/javascript/components/shared/BeltImage';
import ProfileAvatar from 'app/javascript/components/shared/ProfileAvatar';
import CompanySection from 'app/javascript/components/profile/CompanySection';
import ProfileHighlights from 'app/javascript/components/profile/ProfileHighlights';
import { Separator } from 'app/javascript/components/ui/separator';
import SectionHeading from 'app/javascript/components/ui/SectionHeading';
import { getBeltLabel, getBeltSortOrder, getBeltTheme } from 'app/javascript/services/belts';
import { getAgeDivisionLabel, getWeightCategoryLabel } from 'app/javascript/services/cbjjCategories';

interface ProfileHeaderProps {
  profile: PractitionerProfile;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({ profile }) => {
  const highestRankedBelt = [...profile.beltHistory].sort(
    (a, b) => getBeltSortOrder(b.rank) - getBeltSortOrder(a.rank)
  )[0];

  const currentBelt: BeltRankValue = highestRankedBelt?.rank || 'white';
  const currentBeltLabel = getBeltLabel(currentBelt);
  const promotionDate = highestRankedBelt?.date;
  const theme = getBeltTheme(currentBelt);

  const beltCount = profile.beltHistory.filter((b) => b.rank !== 'white').length;

  const categoryTags = [
    getAgeDivisionLabel(profile.competitionCategories?.ageDivision),
    getWeightCategoryLabel(profile.competitionCategories?.weightCategory),
  ].filter((label): label is string => label !== null);

  return (
    <div className="mb-8">
      <div className="mb-6 flex flex-col items-center gap-6 md:flex-row md:items-start md:gap-8">
        <div className="shrink-0">
          <ProfileAvatar photoUrl={profile.photoUrl} name={profile.name} rank={currentBeltLabel} size="md" />
        </div>

        <div className="flex-1 text-center md:text-left">
          {profile.nickname && (
            <span className="mb-1 inline-flex items-center gap-1.5 text-sm font-medium text-yellow-400">
              <Sparkles className="size-3.5" />
              {profile.nickname}
            </span>
          )}
          <h1 className="mb-3 font-bebas text-4xl tracking-tight text-foreground md:text-5xl">{profile.name}</h1>

          <div className="inline-flex items-center gap-3">
            <div className={`rounded-lg p-1.5 ${theme.bg}`}>
              <BeltImage rank={currentBelt} size="xs" className="md:w-12" />
            </div>
            <div className="text-left">
              <span className="block text-base font-bold text-foreground">Faixa {currentBeltLabel}</span>
              {promotionDate && (
                <span className="flex items-center gap-1.5 text-[13px] text-muted-foreground sm:text-xs">
                  <Calendar className="size-3 shrink-0" />
                  {parseISO(promotionDate).toLocaleDateString('pt-BR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
              )}
            </div>
          </div>

          {categoryTags.length > 0 && (
            <div className="mt-3 flex flex-wrap justify-center gap-2 md:justify-start">
              {categoryTags.map((label) => (
                <span
                  key={label}
                  className="inline-flex items-center rounded-lg bg-card px-3 py-1.5 text-sm font-bold uppercase tracking-wide text-foreground"
                >
                  {label}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="hidden md:block">
          <ProfileHighlights
            startDate={profile.startDate}
            beltCount={beltCount}
            birthday={profile.birthday}
            monthlyTrainingCount={profile.monthlyTrainingCount}
          />
        </div>
      </div>

      <div className="mb-6 md:hidden">
        <ProfileHighlights
          startDate={profile.startDate}
          beltCount={beltCount}
          birthday={profile.birthday}
          monthlyTrainingCount={profile.monthlyTrainingCount}
        />
      </div>

      <Separator />

      <div className="pt-6">
        <SectionHeading title="Seu Time" className="mb-4" />
        <CompanySection companies={profile.companies} />
      </div>
    </div>
  );
};

export default ProfileHeader;
